/**
 * F-132 + F-160 (C5): PresenceSwitcher.
 * Pill toggle: online / away / busy / vacation + an inline status message
 * textarea (≤120 chars) that only surfaces for the non-online states.
 *
 * Persists via PATCH /specialists/me/. Vacation auto-clears accepting_orders
 * server-side. The message edit is debounced (500ms after last keystroke) so
 * we don't fire a PATCH per character.
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Textarea, toast } from '@/components/atoms';
import { specialists as api } from '@/api';
import { logger } from '@/utils/logger';
import type { PresenceStatus, SpecialistProfile } from '@/types/domain';
import styles from './PresenceSwitcher.module.css';

const PILLS: { value: PresenceStatus; label: string }[] = [
  { value: 'online', label: 'Онлайн' },
  { value: 'away', label: 'Відсутня' },
  { value: 'busy', label: 'Зайнята' },
  { value: 'vacation', label: 'Відпустка' },
];

const MESSAGE_MAX = 120;
const MESSAGE_DEBOUNCE_MS = 500;
const MESSAGE_STATUSES: PresenceStatus[] = ['away', 'busy', 'vacation'];

export interface PresenceSwitcherProps {
  initial?: Partial<Pick<SpecialistProfile, 'presence_status' | 'presence_message'>>;
  onChange?: (next: SpecialistProfile) => void;
}

export function PresenceSwitcher({ initial, onChange }: PresenceSwitcherProps): JSX.Element {
  const [status, setStatus] = useState<PresenceStatus>(initial?.presence_status ?? 'online');
  const [message, setMessage] = useState<string>(initial?.presence_message ?? '');
  const [busy, setBusy] = useState(false);
  // The last value successfully persisted; lets the debounce skip when the
  // textarea hasn't actually drifted (e.g. focus changes, status toggles).
  const lastSavedMessage = useRef<string>(initial?.presence_message ?? '');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = async (
    next: PresenceStatus,
    nextMessage: string | undefined = undefined,
  ): Promise<void> => {
    setBusy(true);
    try {
      const patch: Record<string, unknown> = { presence_status: next };
      if (nextMessage !== undefined) patch.presence_message = nextMessage;
      const updated = (await api.updateMySpecialist(patch)) as SpecialistProfile;
      setStatus(updated.presence_status ?? next);
      setMessage(updated.presence_message ?? '');
      lastSavedMessage.current = updated.presence_message ?? '';
      onChange?.(updated);
    } catch (err) {
      logger.error('presence save failed', err);
      toast.error('Не вдалось зберегти статус');
    } finally {
      setBusy(false);
    }
  };

  const showMessageInput = MESSAGE_STATUSES.includes(status);

  // Debounced PATCH on message change — only fires when the textarea actually
  // surfaces (away/busy/vacation) and the value differs from the last save.
  useEffect(() => {
    if (!showMessageInput) return;
    if (message === lastSavedMessage.current) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      void save(status, message);
    }, MESSAGE_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // `save` is intentionally excluded — it doesn't change identity for the
    // debounce window and re-creating the timer per render is wasteful.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, status, showMessageInput]);

  return (
    <Card>
      <div className={styles.pills} role="group" aria-label="Статус присутності">
        {PILLS.map((p) => (
          <Button
            key={p.value}
            variant={p.value === status ? 'primary' : 'ghost'}
            compact
            disabled={busy}
            onClick={() => void save(p.value)}
            aria-pressed={p.value === status}
          >
            {p.label}
          </Button>
        ))}
      </div>
      {showMessageInput ? (
        <Textarea
          placeholder="Опиши коротко що зараз (≤120 символів)"
          value={message}
          maxLength={MESSAGE_MAX}
          rows={2}
          onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
          style={{ marginTop: 8 }}
        />
      ) : null}
    </Card>
  );
}
